import { useEffect, useState } from 'react'
import { fetchPublicConfig, getRichmenuLink } from '../../lib/api'
import { useLiff } from '../../hooks/useLiff'

type State = 'initializing' | 'loggingIn' | 'redirecting' | 'error'

const STATUS_MESSAGE: Record<State, string> = {
  initializing: 'LIFFを初期化しています…',
  loggingIn: 'LINEアカウントにログインしています…',
  redirecting: '遷移先へ移動しています…',
  error: 'エラーが発生しました。',
}

export default function LiffRichmenuPage() {
  const [state, setState] = useState<State>('initializing')
  const [errorDetail, setErrorDetail] = useState('')
  const [liffId, setLiffId] = useState('')

  // Fetch LIFF ID from config
  useEffect(() => {
    let cancelled = false
    fetchPublicConfig()
      .then((config) => {
        if (!cancelled && config.liffRichmenuAppId) {
          setLiffId(config.liffRichmenuAppId)
        } else if (!cancelled) {
          setState('error')
          setErrorDetail('LIFF IDが設定されていません。')
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setState('error')
          setErrorDetail(err instanceof Error ? err.message : '設定の取得に失敗しました。')
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const { userId, isLoading: liffLoading, error: liffError, isLoggedIn } = useLiff({
    liffId,
    autoLogin: true,
  })

  // Handle LIFF states
  useEffect(() => {
    if (!liffId) return // Wait for liffId

    if (liffLoading) {
      setState('initializing')
      return
    }

    if (liffError) {
      setState('error')
      setErrorDetail(liffError)
      return
    }

    if (!isLoggedIn) {
      setState('loggingIn')
      return
    }

    // We have a logged-in user, proceed to get redirect URL
    const params = new URLSearchParams(window.location.search)
    const target = params.get('target') || 'billing'
    const explicitRedirect = params.get('redirect')

    getRichmenuLink(target, userId || undefined)
      .then((result) => {
        let redirectUrl: string | null = null

        if (explicitRedirect) {
          try {
            const u = new URL(explicitRedirect, window.location.origin)
            if (target === 'ticket10' && !u.searchParams.has('billing')) {
              u.searchParams.set('billing', 'true')
            }
            redirectUrl = u.toString()
          } catch {
            redirectUrl = explicitRedirect
          }
        } else if (result.url) {
          redirectUrl = result.url
        }

        if (!redirectUrl) {
          setState('error')
          setErrorDetail(result.error || '遷移先URLが取得できませんでした。')
          return
        }

        setState('redirecting')
        window.location.replace(redirectUrl)
      })
      .catch((err) => {
        setState('error')
        setErrorDetail(err instanceof Error ? err.message : '不明なエラーが発生しました。')
      })
  }, [liffId, liffLoading, liffError, isLoggedIn, userId])

  const message = STATUS_MESSAGE[state]

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-gray-50 text-gray-700 px-6">
      <div className="max-w-md w-full text-center space-y-4">
        <div className="text-lg font-medium">{message}</div>
        {state === 'error' && (
          <div className="text-sm text-red-600 whitespace-pre-wrap">{errorDetail || '詳細情報はありません。'}</div>
        )}
        {state === 'error' && (
          <button
            type="button"
            className="mt-2 inline-flex items-center justify-center rounded-md bg-green-500 px-4 py-2 text-white shadow-sm hover:bg-green-600"
            onClick={() => window.location.reload()}
          >
            再読み込み
          </button>
        )}
      </div>
    </main>
  )
}
